import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Check, X, RefreshCw, Layers, ShieldCheck, ChevronDown, ChevronRight, Undo2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    vorschlaegeDurchsicht, allocationsBestaetigen, deleteAllocation, sammelZuruecknehmen,
} from "@/lib/api";
import { euro, germanDate } from "@/lib/format";
import { useSchreibschutz } from "@/lib/auth";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import { Seitenkopf } from "@/hilfe/Seitenkopf";

// Jeder Name führt dorthin, wo er zu Hause ist: Mieter in die Mieterliste mit
// geöffnetem Datensatz, Objekt auf die Immobilie, Einheit auf die Einheit.
// Ohne das lässt sich eine Zuordnung nicht beurteilen, ohne die Seite zu
// verlassen und sich zurückzuarbeiten (Alexander, 03.11.2026).
const Ort = ({ z, className = "" }) => {
    const teile = [];
    if (z.immobilie) {
        teile.push(z.property_id
            ? <Link key="p" to={`/immobilien/${z.property_id}`} className="hover:underline">{z.immobilie}</Link>
            : <span key="p">{z.immobilie}</span>);
    }
    if (z.einheit) {
        teile.push(z.unit_id
            ? <Link key="u" to={`/einheiten/${z.unit_id}`} className="hover:underline">{z.einheit}</Link>
            : <span key="u">{z.einheit}</span>);
    }
    // Ältere Serverantworten kennen die Teile nicht -- dann die fertige Zeile.
    if (!teile.length) return <span className={className}>{z.ort}</span>;
    return (
        <span className={className}>
            {teile.map((t, i) => <React.Fragment key={i}>{i > 0 && " · "}{t}</React.Fragment>)}
        </span>
    );
};

const Mieter = ({ id, name, className = "" }) => {
    if (!name) return null;
    if (!id) return <span className={className}>{name}</span>;
    return <Link to={`/mieter?edit=${id}`} className={`${className} hover:underline`}>{name}</Link>;
};

// Die Durchsicht der Zuordnungsvorschläge. Aus 571 Einzelentscheidungen werden
// 323, weil zwei Bündelungen greifen, die sich nicht überschneiden:
//   Gruppen  — ein Mietverhältnis, dessen Jahr gleichförmig ist (jede Periode
//              genau das Soll, immer derselbe Einzahler). Eine Aussage über
//              die Bank, nicht über die Regel.
//   Sichere  — Sicherheit ≥ 0,9 und höchstens sieben Tage von der Fälligkeit.
//              Liegt nur auf Einheitenkonten, wo das Konto die Einheit kennt.
// Was in keine fällt, bleibt Einzelfall mit seinem Grund.

const Durchsicht = () => {
    const [daten, setDaten] = useState(null);
    const [laedt, setLaedt] = useState(true);
    const [busy, setBusy] = useState(null);
    const [offen, setOffen] = useState(() => new Set());
    const [letzterLauf, setLetzterLauf] = useState(null);
    const { gesperrt, grund } = useSchreibschutz();

    const laden = async () => {
        setLaedt(true);
        try { setDaten(await vorschlaegeDurchsicht()); }
        catch (e) { toast.error("Durchsicht nicht geladen", { description: e?.response?.data?.detail }); }
        finally { setLaedt(false); }
    };
    useEffect(() => { laden(); }, []);

    const bestaetigen = async (ids, grundText, schluessel) => {
        setBusy(schluessel);
        try {
            const r = await allocationsBestaetigen(ids, { apply: true, grund: grundText });
            setLetzterLauf({ lauf: r.lauf, anzahl: r.bestaetigt, summe: r.summe });
            toast.success(`${r.bestaetigt} Zuordnungen bestätigt · ${euro(r.summe)}`, {
                description: r.abgelehnt?.length ? `${r.abgelehnt.length} übersprungen` : undefined,
            });
            await laden();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bestätigen fehlgeschlagen");
        } finally { setBusy(null); }
    };

    const zuruecknehmen = async () => {
        if (!letzterLauf?.lauf) return;
        setBusy("undo");
        try {
            const r = await sammelZuruecknehmen(letzterLauf.lauf);
            toast.success(`${r.zurueckgenommen} Zuordnungen zurück auf Vorschlag`);
            setLetzterLauf(null);
            await laden();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Zurücknehmen fehlgeschlagen");
        } finally { setBusy(null); }
    };

    const ablehnen = async (id) => {
        setBusy(id);
        try { await deleteAllocation(id); await laden(); toast.success("Vorschlag verworfen"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Verwerfen fehlgeschlagen"); }
        finally { setBusy(null); }
    };

    const umschalten = (k) => setOffen((alt) => {
        const n = new Set(alt);
        if (n.has(k)) n.delete(k); else n.add(k);
        return n;
    });

    const einzelne = daten?.einzelne || [];
    const searchFields = useMemo(() => ["tenant_name", "ort", "einzahler", "zweck", "period"], []);
    const { query, setQuery, filtered, total, matches, active } = useFulltextFilter(einzelne, searchFields);

    const gruppen = daten?.gruppen || [];
    const sichere = daten?.sichere || [];

    return (
        <div className="px-6 md:px-10 py-8 space-y-6" data-testid="durchsicht">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <Link to="/mietkonto" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-slate-900 mb-2">
                        <ArrowLeft size={12} /> Mietkonto
                    </Link>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="durchsicht-titel">
                        Durchsicht der Zuordnungen
                    </h1>
                    <Seitenkopf id="seite.durchsicht" />
                </div>
                <div className="flex items-center gap-2">
                    {letzterLauf?.lauf && (
                        <Button variant="outline" size="sm" onClick={zuruecknehmen} disabled={busy === "undo"}
                                data-testid="durchsicht-undo" title={`Lauf ${letzterLauf.lauf} zurücknehmen`}>
                            <Undo2 size={13} className="mr-1.5" /> {letzterLauf.anzahl} zurücknehmen
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={laden} disabled={laedt} data-testid="durchsicht-neu">
                        <RefreshCw size={13} className={`mr-1.5 ${laedt ? "animate-spin" : ""}`} /> Neu laden
                    </Button>
                </div>
            </div>

            {daten && (
                <div className="flex items-center gap-3 text-xs text-gray-500" data-testid="durchsicht-zaehler">
                    <span><b className="text-slate-900">{daten.anzahl_vorschlaege}</b> Vorschläge</span>
                    <span>→</span>
                    <span><b className="text-slate-900">{daten.entscheidungen}</b> Entscheidungen</span>
                    <span>·</span>
                    <span>{gruppen.length} Gruppen ({euro(daten.summe_gruppen)})</span>
                    <span>·</span>
                    <span>{sichere.length} sichere ({euro(daten.summe_sichere)})</span>
                    <span>·</span>
                    <span>{einzelne.length} einzeln</span>
                </div>
            )}

            {/* ------------------------------------------------ Gleichförmige Gruppen */}
            {gruppen.length > 0 && (
                <div className="bg-white border border-emerald-200 rounded-md overflow-hidden" data-testid="gruppen">
                    <div className="px-5 py-3 bg-emerald-50 flex items-start gap-2">
                        <Layers size={15} className="text-emerald-700 mt-0.5 shrink-0" />
                        <div>
                            <div className="font-medium text-slate-900">
                                Gleichförmige Jahre · {gruppen.length} Mietverhältnisse
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                Jede Periode trifft genau das Soll, immer derselbe Einzahler, mindestens
                                drei Perioden. Prüfe die Zeile einmal — bestätigt wird das ganze Jahr.
                            </div>
                        </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {gruppen.map((g) => {
                            const k = `g:${g.tenant_id}`;
                            const auf = offen.has(k);
                            // Wie viele Zahlungen je Periode? Trifft die Summe das Soll,
                            // sagt die Gruppenregel ja -- auch wenn zwei Zahlungen
                            // zusammenkommen. Genau das gehoert beim Aufklappen sichtbar.
                            const zeilen = g.zeilen || [];
                            const proPeriode = zeilen.reduce(
                                (m, z) => ({ ...m, [z.period]: (m[z.period] || 0) + 1 }), {});
                            return (
                                <div key={k} data-testid={`gruppe-${g.tenant_id}`}>
                                    <div className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50">
                                        <button type="button" onClick={() => umschalten(k)} className="text-gray-400 shrink-0">
                                            {auf ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        </button>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-slate-900 font-medium truncate">
                                                <Mieter id={g.tenant_id} name={g.tenant_name} />
                                            </div>
                                            <div className="text-xs text-gray-500 truncate">
                                                <Ort z={g} /> · {g.begruendung}
                                                {g.zahler && <> · zahlt als „{g.zahler}"</>}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="tabular-nums text-slate-900">{euro(g.summe)}</div>
                                            <div className="text-[11px] text-gray-400">{g.anzahl} Vorschläge</div>
                                        </div>
                                        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 h-8 shrink-0"
                                                disabled={gesperrt || busy === k} title={grund}
                                                onClick={() => bestaetigen(g.allocation_ids,
                                                    `gleichförmiges Jahr: ${g.perioden.length} Perioden, ein Zahler`, k)}
                                                data-testid={`gruppe-ok-${g.tenant_id}`}>
                                            <Check size={13} className="mr-1" /> Jahr bestätigen
                                        </Button>
                                    </div>
                                    {auf && (
                                        <div className="px-5 pb-3 pl-14" data-testid={`gruppe-detail-${g.tenant_id}`}>
                                            <div className="text-xs text-gray-500 mb-1.5">
                                                {g.perioden.length} Perioden · {zeilen.length} Zahlungen · Konto{g.ebenen.length > 1 ? "ebenen" : "ebene"} {g.ebenen.join(", ")}
                                            </div>
                                            <table className="w-full text-xs">
                                                <tbody className="divide-y divide-gray-100">
                                                    {zeilen.map((z, i) => {
                                                        const neu = i === 0 || zeilen[i - 1].period !== z.period;
                                                        const anzahl = proPeriode[z.period] || 1;
                                                        return (
                                                            <tr key={z.allocation_id} className="align-baseline">
                                                                <td className="py-1 pr-3 w-16 whitespace-nowrap text-gray-400">
                                                                    {neu ? z.period : ""}
                                                                </td>
                                                                <td className="py-1 pr-3 w-20 whitespace-nowrap text-gray-500">
                                                                    {germanDate(z.buchungsdatum)}
                                                                </td>
                                                                <td className="py-1 pr-3 text-slate-700 truncate max-w-[180px]" title={z.einzahler || ""}>
                                                                    {z.einzahler}
                                                                </td>
                                                                <td className="py-1 pr-3 text-gray-500 truncate" title={z.zweck || ""}>
                                                                    {z.zweck}
                                                                </td>
                                                                <td className="py-1 w-20 text-right tabular-nums text-slate-900">
                                                                    {euro(z.betrag)}
                                                                </td>
                                                                <td className="py-1 pl-3 w-36 text-right whitespace-nowrap text-gray-400">
                                                                    {neu && (anzahl > 1
                                                                        ? <span className="text-amber-700">{anzahl} Zahlungen · Soll {euro(z.soll)}</span>
                                                                        : <>Soll {euro(z.soll)}</>)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* -------------------------------------------------- Sichere Einzelne */}
            {sichere.length > 0 && (
                <div className="bg-white border border-sky-200 rounded-md overflow-hidden" data-testid="sichere">
                    <div className="px-5 py-3 bg-sky-50 flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2">
                            <ShieldCheck size={15} className="text-sky-700 mt-0.5 shrink-0" />
                            <div>
                                <div className="font-medium text-slate-900">
                                    Sichere Einzelne · {sichere.length} Zuordnungen · {euro(daten.summe_sichere)}
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                    Das Konto gehört der Einheit, und die Zahlung liegt höchstens sieben Tage
                                    von der Fälligkeit. Unten einzeln nachlesbar.
                                </div>
                            </div>
                        </div>
                        <Button size="sm" className="bg-sky-700 hover:bg-sky-800 h-8 shrink-0"
                                disabled={gesperrt || busy === "sicher"} title={grund}
                                onClick={() => bestaetigen(sichere.map((z) => z.allocation_id),
                                    "Sicherheit ≥ 0,9 und höchstens sieben Tage von der Fälligkeit", "sicher")}
                                data-testid="sichere-ok">
                            <Check size={13} className="mr-1" /> Alle {sichere.length} bestätigen
                        </Button>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                        {sichere.map((z) => (
                            <div key={z.allocation_id} className="px-5 py-2 flex items-baseline gap-3 text-xs">
                                <span className="text-gray-400 w-16 shrink-0">{z.period}</span>
                                <span className="text-slate-800 truncate flex-1">
                                    <Ort z={z} /> · <Mieter id={z.tenant_id} name={z.tenant_name} />
                                </span>
                                <span className="text-gray-500 truncate max-w-[180px]">{z.einzahler}</span>
                                <span className="text-gray-400 w-14 text-right">{z.tage >= 0 ? `+${z.tage}` : z.tage} T.</span>
                                <span className="tabular-nums text-slate-900 w-20 text-right">{euro(z.betrag)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ------------------------------------------------------- Einzelfälle */}
            <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="einzelne">
                <div className="px-5 py-3 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="font-medium text-slate-900">Einzeln zu prüfen · {einzelne.length}</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                            Jede Zeile nennt, warum der Automat sie vorschlägt — und was nicht passt.
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <SearchBox value={query} onChange={setQuery} placeholder="Mieter, Objekt, Einzahler…"
                                   testid="einzelne-suche" className="w-[260px]" />
                        <MatchCount active={active} matches={matches} total={total} />
                    </div>
                </div>
                <div className="divide-y divide-gray-100">
                    {filtered.slice(0, 120).map((z) => {
                        const abweichung = Math.abs(z.betrag - z.soll) > 0.005;
                        return (
                            <div key={z.allocation_id} className="px-5 py-3 hover:bg-gray-50 flex items-start gap-3"
                                 data-testid={`einzeln-${z.allocation_id}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <Ort z={z} className="text-slate-900" />
                                        <Mieter id={z.tenant_id} name={z.tenant_name} className="text-xs text-gray-500" />
                                        <span className="text-[11px] text-gray-400">{z.period}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                            Sicherheit {z.sicherheit ?? "—"}
                                        </span>
                                        {abweichung && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                                Soll {euro(z.soll)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5 truncate" title={z.grund || ""}>
                                        {z.einzahler} · {germanDate(z.buchungsdatum)}
                                        {z.tage != null && <> ({z.tage >= 0 ? `+${z.tage}` : z.tage} Tage)</>}
                                        {z.zweck && <> · {z.zweck}</>}
                                    </div>
                                </div>
                                <div className="tabular-nums text-slate-900 shrink-0">{euro(z.betrag)}</div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <Button size="sm" variant="outline" className="h-7 px-2"
                                            disabled={gesperrt || busy === z.allocation_id} title={grund || "Bestätigen"}
                                            onClick={() => bestaetigen([z.allocation_id], "einzeln geprüft", z.allocation_id)}>
                                        <Check size={12} />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-red-600"
                                            disabled={gesperrt || busy === z.allocation_id} title="Vorschlag verwerfen"
                                            onClick={() => ablehnen(z.allocation_id)}>
                                        <X size={12} />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                    {filtered.length > 120 && (
                        <div className="px-5 py-3 text-xs text-gray-400 text-center">
                            … {filtered.length - 120} weitere. Suche eingrenzen oder Gruppen zuerst bestätigen.
                        </div>
                    )}
                    {!filtered.length && (
                        <div className="px-5 py-12 text-center text-sm text-gray-400">
                            {laedt ? "Lädt …" : "Keine Einzelfälle — alles gebündelt oder bestätigt."}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Durchsicht;
